import { parsePhoneNumberFromString } from "libphonenumber-js/max";

/**
 * Extracts phone number by removing country code from the beginning
 * @param {string} phoneNumber - The full phone number (e.g., "9193096")
 * @param {string} countryCode - The country code with or without + (e.g., "+91" or "91")
 * @returns {number|null} - Phone number without country code as integer (e.g., 93096), or null if invalid
 */
export const extractPhoneNumber = (phoneNumber, countryCode) => {
    if (!phoneNumber || !countryCode) {
      return phoneNumber ? parseInt(phoneNumber) || null : null;
    }
  
    // Remove + and any spaces from country code, extract only digits
    const countryCodeDigits = countryCode.replace(/[+\s]/g, "");
    
    // Remove leading zeros and spaces from phone number
    const cleanPhoneNumber = String(phoneNumber).trim();
    
    // Check if phone number starts with country code digits
    if (countryCodeDigits && cleanPhoneNumber.startsWith(countryCodeDigits)) {
      // Remove country code digits from the beginning
      const extractedNumber = cleanPhoneNumber.substring(countryCodeDigits.length);
      // Parse to integer, return null if invalid
      return extractedNumber ? parseInt(extractedNumber) || null : null;
    }
    
    // If country code not found at start, parse original number
    return cleanPhoneNumber ? parseInt(cleanPhoneNumber) || null : null;
  };


  export const combinePhoneNumber = (phoneNumber, countryCode) => {
    if (!phoneNumber || !countryCode) {
      return phoneNumber ? String(phoneNumber) : "";
    }
  
    // Remove + and any spaces from country code, extract only digits
    const countryCodeDigits = countryCode.replace(/[+\s]/g, "");
    
    // Convert phone number to string and remove any spaces
    const cleanPhoneNumber = String(phoneNumber).trim().replace(/\s/g, "");
    
    // Combine country code digits with phone number
    return countryCodeDigits + cleanPhoneNumber;
  };

/** Country dropdown search — matches signup PhoneInput. */
export const phoneInputCountrySearchProps = {
  enableSearch: true,
  searchPlaceholder: "Search country",
  searchClass: "country-search",
  dropdownClass: "country-dropdown",
};

/** Value for react-phone-input-2 (digits only, includes dial code when known). */
export const getPhoneInputValue = (phoneNumber, countryCode) => {
  const digits = String(phoneNumber || "").replace(/\D/g, "");
  if (!digits) return "";
  const codeDigits = String(countryCode || "").replace(/\D/g, "");
  if (!codeDigits || digits.startsWith(codeDigits)) return digits;
  return combinePhoneNumber(digits, countryCode);
};


  // export const formatPhoneNumber = (countryCode, phoneNumber) => {
  //   if (!phoneNumber) return "-";
  
  //   const cleaned = phoneNumber.replace(/\D/g, "");
  
  //   // US format
  //   if (countryCode === "+1" && cleaned.length === 10) {
  //     return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  //   }
  
  //   // fallback → global formatting
  //   try {
  //     const raw = `${countryCode || ""}${phoneNumber}`;
  //     const parsed = parsePhoneNumberFromString(raw);
  //     return parsed ? parsed.formatInternational() : `${countryCode || ""} ${phoneNumber}`;
  //   } catch {
  //     return `${countryCode || ""} ${phoneNumber}`;
  //   }
  // };
  // export const formatPhoneNumber = (countryCode, phoneNumber) => {
  //   if (!phoneNumber) return "-";
  
  //   const cleaned = phoneNumber.replace(/\D/g, "");
  
  //   // Custom US style (your preferred version)
  //   // if (countryCode === "+1" && cleaned.length === 10) {
  //   //   return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  //   // }
  
  //   // Every other country → official telephone format
  //   try {
  //     const parsed = parsePhoneNumberFromString(`${countryCode}${cleaned}`);
  //     if (parsed) return parsed.formatInternational();
  //   } catch(error){
  //     console.log("error in formatPhoneNumber : ", error);
  //   }
  
  //   return `${countryCode} ${cleaned}`;
  // };
  
  // export const formatPhoneNumber = (countryCode, phoneNumber) => {
  //   if (!phoneNumber) return "-";
  
  //   const cleaned = phoneNumber.replace(/\D/g, "");
  //   const full = `${countryCode}${cleaned}`;
  
  //   try {
  //     const parsed = parsePhoneNumberFromString(full);
  
  //     if (parsed) {
  //       return `+${parsed.countryCallingCode} ${parsed.formatNational()}`;
  //     }
  //   } catch (e) {
  //     console.log("formatPhoneNumber error:", e);
  //   }
  
  //   return `${countryCode} ${cleaned}`;
  // };

  /**
 * Formats phone number based on country code
 * @param {string} countryCode - The country dial code (e.g., "+1", "+91", "1")
 * @param {string|number} phoneNumber - The phone number
 * @returns {string} - Formatted phone number
 */
export const formatPhoneNumber = (countryCode, phoneNumber) => {
  if (!phoneNumber) return "-";

  // Clean country code - remove + and spaces, keep only digits
  const cleanCountryCode = countryCode ? String(countryCode).replace(/[+\s]/g, '') : '';
  
  // Extract only digits from phone number
  const digits = String(phoneNumber).replace(/\D/g, '');
  
  if (!digits) return "-";

  // Map dial codes to country codes
  const dialCodeToCountryCode = {
    '1': 'US',      // Default to US for +1 (could be CA too)
    '44': 'GB',
    '61': 'AU',
    '81': 'JP',
    '49': 'DE',
    '33': 'FR',
    '971': 'AE',
    '974': 'QA',
    '965': 'KW',
    '973': 'BH',
    '968': 'OM',
    '65': 'SG',
    '966': 'SA',
    '91': 'IN',
  };

  // Get country code from dial code
  const country = {
    countryCode: dialCodeToCountryCode[cleanCountryCode],
    dialCode: cleanCountryCode
  };

  // If country is not in our custom switch, use library
  if (!country.countryCode) {
    try {
      const fullNumber = cleanCountryCode ? `+${cleanCountryCode}${digits}` : digits;
      const parsed = parsePhoneNumberFromString(fullNumber);
      
      if (parsed) {
        return `+${parsed.countryCallingCode} ${parsed.formatNational()}`;
      }
    } catch (e) {
      console.log("formatPhoneNumber library error:", e);
    }
    
    // Fallback if library parsing fails
    return cleanCountryCode ? `+${cleanCountryCode} ${digits}` : digits;
  }

  // Format the local number based on country using custom switch
  let formattedLocal = '';
  
  switch (country.countryCode) {
    case 'US':
    case 'CA':
      // Format as (XXX) XXX-XXXX
      if (digits.length <= 3) formattedLocal = digits;
      else if (digits.length <= 6) formattedLocal = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      else formattedLocal = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
      break;

    case 'GB':
      // Format as XXXX XXX XXX
      if (digits.length <= 4) formattedLocal = digits;
      else if (digits.length <= 7) formattedLocal = `${digits.slice(0, 4)} ${digits.slice(4)}`;
      else formattedLocal = `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 10)}`;
      break;

    case 'AU':
      // Format as XXX XXX XXX
      if (digits.length <= 3) formattedLocal = digits;
      else if (digits.length <= 6) formattedLocal = `${digits.slice(0, 3)} ${digits.slice(3)}`;
      else formattedLocal = `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)}`;
      break;

    case 'JP':
      // Format as XX-XXXX-XXXX
      if (digits.length <= 2) formattedLocal = digits;
      else if (digits.length <= 6) formattedLocal = `${digits.slice(0, 2)}-${digits.slice(2)}`;
      else formattedLocal = `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6, 11)}`;
      break;

    case 'DE':
      // Format as XXX XXXXXXXXX
      if (digits.length <= 3) formattedLocal = digits;
      else formattedLocal = `${digits.slice(0, 3)} ${digits.slice(3, 12)}`;
      break;

    case 'FR':
      // Format as X XX XX XX XX
      if (digits.length <= 1) formattedLocal = digits;
      else if (digits.length <= 3) formattedLocal = `${digits.slice(0, 1)} ${digits.slice(1)}`;
      else if (digits.length <= 5) formattedLocal = `${digits.slice(0, 1)} ${digits.slice(1, 3)} ${digits.slice(3)}`;
      else if (digits.length <= 7) formattedLocal = `${digits.slice(0, 1)} ${digits.slice(1, 3)} ${digits.slice(3, 5)} ${digits.slice(5)}`;
      else formattedLocal = `${digits.slice(0, 1)} ${digits.slice(1, 3)} ${digits.slice(3, 5)} ${digits.slice(5, 7)} ${digits.slice(7, 9)}`;
      break;

    case 'AE':
    case 'QA':
    case 'KW':
    case 'BH':
    case 'OM':
    case 'SG':
      // Format as XXXX XXXX
      if (digits.length <= 4) formattedLocal = digits;
      else formattedLocal = `${digits.slice(0, 4)} ${digits.slice(4, 8)}`;
      break;

    case 'SA':
      // Format as 5X XXX XXXX
      if (digits.length <= 2) formattedLocal = digits;
      else if (digits.length <= 5) formattedLocal = `${digits.slice(0, 2)} ${digits.slice(2)}`;
      else formattedLocal = `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 9)}`;
      break;

    case 'IN':
      // Format as (XXX) - (XXX) for India
      if (digits.length <= 3) formattedLocal = digits;
      else if (digits.length <= 6) formattedLocal = `(${digits.slice(0, 3)}) - (${digits.slice(3)})`;
      else formattedLocal = `(${digits.slice(0, 3)}) - (${digits.slice(3, 6)})${digits.slice(6)}`;
      break;
  }

  // Return formatted number with country code prefix
  if (cleanCountryCode) {
    return `+${cleanCountryCode} ${formattedLocal}`;
  }
  
  return formattedLocal || digits;
};
